import React, { PureComponent } from 'react';
import {
  StyleSheet, View, Text,
  SafeAreaView,
  TouchableOpacity,
} from 'react-native';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { defaultScale } from '../../../../utils/scaling';
import { getNextStep, navigateToNextStep } from '../../../actions';
import { IAppStore } from '../../../../HomeWorkouts';
import { GoogleAnalytics } from '../../../../services/google-analytics';
import Theme from '../../../../theme';
import { UserDataConsumer } from '../../../../utils';
import GenericSmartWatchImage from '../../../../assets/icons/connected-apps/generic-smart-watch.svg';
import GreenCheckMark from '../../../../assets/icons/check-circle-green.svg';
import { IConnectedAppOnboardingProps, navigateToNextOnboardingStep } from '../ConnectedAppEntryPointScreen';
import FooterButtons from './FooterButtons';
import { IAppForOnboarding } from '../../../../ConnectedApps';
import MultiStyleText from '../../../../ui/components/MultiStyleText';
import { openInAppWebViewerInBackground } from '../../../../utils/inAppWebViewer';
import { getUrls } from '../../../../config';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.WHITE,
    paddingVertical: defaultScale(16),
  },
  headerContainer: {
    alignItems: 'center',
    marginTop: '10%',
    textAlign: 'center',
    flex: 1,
  },
  heading: {
    textAlign: 'center',
    color: Theme.GREEN,
    marginLeft: defaultScale(10),
  },
  header: {
    marginTop: defaultScale(10),
    marginBottom: defaultScale(40),
    color: Theme.BLACK,
    paddingHorizontal: defaultScale(10),
    textAlign: 'center',
  },
  genericSmartWatchIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: defaultScale(30),
  },
  subHeader: {
    marginTop: defaultScale(40),
    color: Theme.GREY_DARK_MEDIUM,
    paddingHorizontal: defaultScale(30),
    textAlign: 'center',
  },
});

class DoYouHaveATrackingDevice extends PureComponent<IConnectedAppOnboardingProps & WithTranslation> {
  private readonly googleAnalytics: GoogleAnalytics;

  private readonly appForOnboarding: IAppForOnboarding = this.props.navigation.getParam('appForOnboarding');

  public constructor(props: IConnectedAppOnboardingProps & WithTranslation) {
    super(props);
    this.googleAnalytics = new GoogleAnalytics();
  }

  public render() {
    const { t } = this.props;

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.headerContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <GreenCheckMark />
            <MultiStyleText
              templateString={t('appEnabled')}
              styleKeys={[
                {
                  keyName: 'connectedApp',
                  keyValue: this.appForOnboarding.app,
                },
              ]}
              normalStyle={{ ...Theme.textStyle.bodyCopyBold, ...styles.heading }}
            />
          </View>
          <Text style={[Theme.textStyle.heading, styles.header]}>{t('doYouHaveATrackingDevice')}</Text>
          <View style={styles.genericSmartWatchIcon}>
            <GenericSmartWatchImage style={{ transform: [{ scale: 1.5 }] }} />
          </View>
          <MultiStyleText
            templateString={t('enableAppOnYourDevice')}
            styleKeys={[
              {
                keyName: 'connectedApp',
                keyValue: this.appForOnboarding.app,
              },
              {
                keyName: 'onYourDevicesApp',
                keyValue: 'on your device\'s app',
                style: { ...Theme.textStyle.bodyCopyBold, ...styles.subHeader },
              },
            ]}
            normalStyle={{ ...Theme.textStyle.bodyCopyReg, ...styles.subHeader }}
          />
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: defaultScale(20) }}>
            <TouchableOpacity onPress={() => { openInAppWebViewerInBackground(this.learnMoreLink()); }}>
              <Text style={[Theme.textStyle.bodyCopyBold, { color: Theme.PRIMARY }]}>
                {t('learnMore')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        <FooterButtons
          primaryButtonText={t('yesIHaveADevice')}
          onPrimaryButtonPress={() => this.proceedToNextStep()}
          secondaryButtonText={t('noIDontHaveADevice')}
          onSecondaryButtonPress={() => this.noDevice()}
          isLoading={false}
        />
      </SafeAreaView>
    );
  }

  private learnMoreLink = () => {
    const app = this.appForOnboarding.app;

    switch (app) {
      case 'Apple Health':
        return getUrls().FAQ_CONNECT_TRACKING_DEVICE_IOS;
      case 'Health Connect':
        return getUrls().FAQ_CONNECT_TRACKING_DEVICE_ANDROID;
    }
    return getUrls().HEART_RATE_FAQ;
  };

  private proceedToNextStep = () => {
    // Todo: navigate to next screens in V2, but skip that and continue with onboarding for now
    navigateToNextOnboardingStep(
      this.props,
      this.props.getNextStep,
      this.props.navigateToNextStep,
    );
  };

  private noDevice = () => {
    navigateToNextOnboardingStep(
      this.props,
      this.props.getNextStep,
      this.props.navigateToNextStep,
    );
    this.googleAnalytics.logEventWithParamaters('sign_up_do_you_have_a_tracking_device', { enabled: false });
  };
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: state.auth.signup.enabledFeatures,
});

const mapDispatchToProps = {
  getNextStep,
  navigateToNextStep,
};

export default withTranslation(['auth', 'settings'])(
  connect(mapStateToProps, mapDispatchToProps)(UserDataConsumer(DoYouHaveATrackingDevice)),
);
